import { FieldValues, Path, UseFormRegister } from "react-hook-form";
import classNames from "classnames";
import { CheckboxProps } from "@/app/types/props/forms/CheckboxProps";
import { useEffect, useRef } from "react";

interface RegisterReturn {
  ref?: (instance: HTMLInputElement | null) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  name?: string;
}

const Checkbox = <TFormValues extends FieldValues>({
  label,
  name,
  register,
  error,
  disabled,
  className,
  checked,
  onCheckedChange,
  "aria-label": ariaLabel,
}: CheckboxProps<TFormValues>) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = checked === "indeterminate";
    }
  }, [checked]);

  type RegisterProps = ReturnType<UseFormRegister<TFormValues>> | RegisterReturn;

  const registerProps =
    register && name
      ? (register(name as Path<TFormValues>) as RegisterProps)
      : ({
          ref: undefined,
          onChange: undefined,
          onBlur: undefined,
          name: undefined,
        } as RegisterReturn);

  const { ref: registerRef, onChange: registerOnChange, ...registerRest } = registerProps;

  return (
    <div className="flex flex-col gap-1">
      <label className="flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          ref={(element) => {
            inputRef.current = element;
            if (typeof registerRef === "function") {
              registerRef(element);
            }
          }}
          {...registerRest}
          disabled={disabled}
          checked={checked === "indeterminate" ? false : checked}
          onChange={(e) => {
            if (registerOnChange) {
              registerOnChange(e);
            }
            onCheckedChange?.(e.target.checked);
          }}
          aria-label={ariaLabel}
          aria-checked={checked === "indeterminate" ? "mixed" : checked}
          className={classNames(
            "focus:ring-blue/20 h-4 w-4 rounded border-gray-300 text-xBlue",
            {
              "cursor-not-allowed opacity-60": disabled,
              "border-red-500": error,
            },
            className
          )}
        />
        {label && (
          <span
            className={classNames("text-sm font-medium", {
              "text-gray-500": !error,
              "text-red-500": error,
              "opacity-60": disabled,
            })}
          >
            {label}
          </span>
        )}
      </label>
      {error?.message && <p className="ml-6 text-sm text-red-500">{error.message}</p>}
    </div>
  );
};

export default Checkbox;
